"use client";

import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

const STATUS_JSON_URL = "https://happyuptime.com/api/v1/widgets/PIDr8apCPvf-ahkS2tiY8/status.json";
const STATUS_PAGE_URL = "https://status.b3os.org";
const REFRESH_INTERVAL_MS = 60_000;

type StatusKind = "operational" | "degraded" | "outage" | "maintenance" | "unknown";

interface StatusJson {
  status?: string;
  overall?: string;
  incidents?: unknown[];
  maintenance?: unknown[];
}

function classifyStatus(raw: string | undefined): StatusKind {
  switch (raw) {
    case "operational":
      return "operational";
    case "degraded":
    case "degraded_performance":
    case "partial_outage":
      return "degraded";
    case "major_outage":
    case "outage":
    case "down":
      return "outage";
    case "maintenance":
    case "under_maintenance":
      return "maintenance";
    default:
      return "unknown";
  }
}

const STATUS_COPY: Record<StatusKind, string> = {
  operational: "All systems operational",
  degraded: "Degraded performance",
  outage: "Service disruption",
  maintenance: "Scheduled maintenance",
  unknown: "Status unavailable",
};

const DOT_CLASSES: Record<StatusKind, string> = {
  operational: "bg-green-500",
  degraded: "bg-amber-500",
  outage: "bg-red-500",
  maintenance: "bg-blue-500",
  unknown: "bg-zinc-400",
};

interface StatusBadgeProps {
  className?: string;
}

export function StatusBadge({ className }: StatusBadgeProps) {
  const [kind, setKind] = useState<StatusKind>("unknown");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(STATUS_JSON_URL, { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as StatusJson;
        if (cancelled) return;
        setKind(classifyStatus(data.overall ?? data.status));
      } catch {
        // Network failure — leave status as-is rather than flipping to "unknown"
        // mid-session; initial load failure stays "unknown" which is accurate.
      }
    };

    void load();
    const id = window.setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return (
    <a
      href={STATUS_PAGE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex items-center gap-2 text-xs font-medium",
        "text-ds-text-secondary hover:text-ds-text-primary transition-colors",
        className,
      )}
      title="View B3OS status"
    >
      <span className="relative flex h-2 w-2 shrink-0">
        <span className={cn("absolute inline-flex h-full w-full rounded-full opacity-40", DOT_CLASSES[kind])} />
        <span className={cn("relative inline-flex h-2 w-2 rounded-full", DOT_CLASSES[kind])} />
      </span>
      <span className="truncate">{STATUS_COPY[kind]}</span>
    </a>
  );
}
